import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { QRCodeCanvas } from 'qrcode.react';
import { 
  ArrowLeft, 
  ArrowRight, 
  Heart, 
  Crown, 
  Sparkles,
  FileText,
  User,
  Home,
  Mail,
  AlertCircle,
  Search,
  MapPin
} from 'lucide-react';
import ElegantSidebar from '../../../shared/components/layout/ElegantSidebar';
import '../styles/QRPaperCommon.css';
import { 
  OrderFormLocationState as LocationState,
  FormData,
  RoomInfo,
  ValidationErrors,
  PostalCodeSearchResult,
  ConsentPageLocationState
} from '../../../shared/types/qrPaper';

const QRPaperOrderForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState;

  const [formData, setFormData] = useState<FormData>({
    userName: '',
    postalCode: '',
    prefecture: '',
    city: '',
    addressLine: '',
    email: ''
  });
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isSearchingPostalCode, setIsSearchingPostalCode] = useState(false);
  const [roomInfo, setRoomInfo] = useState<RoomInfo | null>(null);
  const [isLoadingRoom, setIsLoadingRoom] = useState(true);

  // リダイレクト処理とルーム情報取得
  useEffect(() => {
    if (!state?.roomid || !state?.qrUrl || !state?.selectedDesign) {
      alert('注文情報が不正です。デザイン選択画面に戻ります。');
      navigate('/admin/qr-paper/design');
      return;
    }

    // ルーム情報を取得
    const fetchRoomInfo = async () => {
      try {
        setIsLoadingRoom(true);
        const baseUrl = process.env.REACT_APP_URL || 'http://localhost:8081';
        const response = await fetch(`${baseUrl}/api/room/${state.roomid}`);
        
        if (!response.ok) {
          throw new Error(`HTTP Error: ${response.status}`);
        }

        const data = await response.json();
        
        if (data.success) {
          setRoomInfo({
            roomid: data.roomid,
            coupleName: data.coupleName
          });
        } else {
          console.error('ルーム情報の取得に失敗:', data.message);
          setRoomInfo({
            roomid: state.roomid,
            coupleName: state.roomid // フォールバック
          });
        }
      } catch (error) {
        console.error('ルーム情報取得エラー:', error);
        setRoomInfo({
          roomid: state.roomid,
          coupleName: state.roomid // フォールバック
        });
      } finally {
        setIsLoadingRoom(false);
      }
    };

    fetchRoomInfo();
  }, [state, navigate]);

  // 画面サイズに応じてサイドバー状態を設定
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      const newIsDesktop = width >= 1024;
      setIsDesktop(newIsDesktop);
      
      if (newIsDesktop && !isDesktop) {
        setSidebarOpen(false);
      } else if (!newIsDesktop && isDesktop) {
        setSidebarOpen(false);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isDesktop]);

  // デザイン名を取得
  const getDesignName = (designId: string, designType?: string): string => {
    if (designType === 'original' && designId === 'custom') {
      return 'オリジナルデザイン';
    }
    
    const designs = {
      elegant: 'エレガント',
      romantic: 'ロマンティック',
      sparkle: 'スパークル'
    };
    return designs[designId as keyof typeof designs] || designId;
  };

  // 郵便番号検索機能
  const searchPostalCode = async () => {
    const postalCode = formData.postalCode.replace(/[^0-9]/g, '');
    
    if (postalCode.length !== 7) {
      alert('郵便番号は7桁で入力してください（例：1234567）');
      return;
    }

    setIsSearchingPostalCode(true);

    try {
      // 郵便番号検索API（zipcloud.ibsnet.co.jp）を使用
      const response = await fetch(`https://zipcloud.ibsnet.co.jp/api/search?zipcode=${postalCode}`);
      const data = await response.json();

      if (data.status === 200 && data.results && data.results.length > 0) {
        const result = data.results[0];
        
        // 住所情報を自動入力
        setFormData(prev => ({
          ...prev,
          prefecture: result.address1, // 都道府県
          city: result.address2,       // 市区町村
          addressLine: result.address3 // 町域
        }));

        // エラーをクリア
        setErrors(prev => ({
          ...prev,
          postalCode: undefined,
          prefecture: undefined,
          city: undefined
        }));

        alert('住所を自動入力しました。番地以降を追記してください。');
      } else {
        alert('該当する住所が見つかりませんでした。郵便番号をご確認ください。');
      }
    } catch (error) {
      console.error('郵便番号検索エラー:', error);
      alert('郵便番号検索に失敗しました。手動で住所を入力してください。');
    } finally {
      setIsSearchingPostalCode(false);
    }
  };

  // バリデーション
  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {};

    // ユーザー名バリデーション
    if (!formData.userName.trim()) {
      newErrors.userName = 'お名前は必須です';
    } else if (formData.userName.trim().length < 2) {
      newErrors.userName = 'お名前は2文字以上で入力してください';
    } else if (formData.userName.trim().length > 50) {
      newErrors.userName = 'お名前は50文字以内で入力してください';
    }

    // 郵便番号バリデーション
    const postalCode = formData.postalCode.replace(/[^0-9]/g, '');
    if (!formData.postalCode.trim()) {
      newErrors.postalCode = '郵便番号は必須です';
    } else if (postalCode.length !== 7) {
      newErrors.postalCode = '郵便番号は7桁で入力してください';
    }

    // 都道府県バリデーション
    if (!formData.prefecture.trim()) {
      newErrors.prefecture = '都道府県は必須です';
    } else if (formData.prefecture.trim().length > 10) {
      newErrors.prefecture = '都道府県は10文字以内で入力してください';
    }

    // 市区町村バリデーション
    if (!formData.city.trim()) {
      newErrors.city = '市区町村は必須です';
    } else if (formData.city.trim().length > 50) {
      newErrors.city = '市区町村は50文字以内で入力してください';
    }

    // 番地・建物名バリデーション
    if (!formData.addressLine.trim()) {
      newErrors.addressLine = '番地・建物名は必須です';
    } else if (formData.addressLine.trim().length > 100) {
      newErrors.addressLine = '番地・建物名は100文字以内で入力してください';
    }

    // メールアドレスバリデーション
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      newErrors.email = 'メールアドレスは必須です';
    } else if (!emailRegex.test(formData.email.trim())) {
      newErrors.email = '正しいメールアドレスを入力してください';
    } else if (formData.email.trim().length > 100) {
      newErrors.email = 'メールアドレスは100文字以内で入力してください';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 次画面への遷移処理
  const handleNext = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // 同意確認画面に遷移するためのデータを準備
    const navigationState: ConsentPageLocationState = {
      ...state, // 既存のLocationState (roomid, qrUrl, selectedDesign, designType, originalDesignRequests)
      formData: {
        userName: formData.userName.trim(),
        postalCode: formData.postalCode.trim(),
        prefecture: formData.prefecture.trim(),
        city: formData.city.trim(),
        addressLine: formData.addressLine.trim(),
        email: formData.email.trim()
      }
    };

    console.log('📋 同意確認画面に遷移します:', navigationState);
    
    // 同意確認画面へ遷移
    navigate('/admin/qr-paper/consent', { state: navigationState });
  };

  // 入力値更新
  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // エラーをクリア
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const handleBack = () => {
    navigate('/admin/qr-paper/design', { state: { roomid: state.roomid, qrUrl: state.qrUrl } });
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleSidebarOverlayClick = () => {
    if (!isDesktop) {
      setSidebarOpen(false);
    }
  };

  if (!state?.roomid) {
    return null; // リダイレクト処理中
  }

  return (
    <div className="wedding-admin-layout">
      {/* サイドバーオーバーレイ（モバイル時） */}
      {!isDesktop && sidebarOpen && (
        <div className="sidebar-overlay" onClick={handleSidebarOverlayClick}></div>
      )}
      
      {/* サイドバー */}
      <div className={`sidebar-container ${isDesktop ? 'desktop' : 'mobile'} ${sidebarOpen ? 'open' : ''}`}>
        <ElegantSidebar />
      </div>
      
      <div className={`wedding-main-content ${isDesktop ? 'desktop' : 'mobile'}`}>
        {/* トグルボタン（モバイル時のみ） */}
        {!isDesktop && (
          <button className="sidebar-toggle-button" onClick={toggleSidebar}>
            <FileText size={24} />
          </button>
        )}

        {/* ヘッダーセクション */}
        <div className="wedding-header">
          <div className="wedding-header-decoration">
            <div className="crown-icon">
              <Crown size={32} />
            </div>
            <div className="sparkle-icon sparkle-1">
              <Sparkles size={20} />
            </div>
            <div className="sparkle-icon sparkle-2">
              <Sparkles size={16} />
            </div>
          </div>
          <h1 className="wedding-title">
            QRコード用紙作成
            <Heart className="title-heart" size={40} />
          </h1>
          <p className="wedding-subtitle">Order Information - ご注文情報入力</p>
        </div>

        {/* メインコンテンツ */}
        <div className="qr-paper-content">
          {/* 進行状況インジケーター */}
          <div className="progress-indicator">
            <div className="progress-step completed">
              <div className="step-number">1</div>
              <div className="step-label">デザイン選択</div>
            </div>
            <div className="progress-line active"></div>
            <div className="progress-step active">
              <div className="step-number">2</div>
              <div className="step-label">情報入力</div>
            </div>
            <div className="progress-line"></div>
            <div className="progress-step">
              <div className="step-number">3</div>
              <div className="step-label">同意確認</div>
            </div>
            <div className="progress-line"></div>
            <div className="progress-step">
              <div className="step-number">4</div>
              <div className="step-label">最終確認</div>
            </div>
          </div>

          {/* 選択内容確認 */}
          <div className="order-summary">
            <div className="summary-header">
              <FileText size={20} />
              <span>ご注文内容</span>
            </div>
            <div className="summary-content">
              <div className="summary-item">
                <label>ルーム:</label>
                {isLoadingRoom ? (
                  <span>読み込み中...</span>
                ) : (
                  <span>
                    {roomInfo?.coupleName || state.roomid}
                    {roomInfo?.coupleName && roomInfo.coupleName !== state.roomid && (
                      <span className="room-id-detail"> ({state.roomid})</span>
                    )}
                  </span>
                )}
              </div>
              <div className="summary-item">
                <label>選択デザイン:</label>
                <span>{getDesignName(state.selectedDesign, state.designType)}</span>
              </div>
            </div>
          </div>

          {/* 注文フォーム */}
          <div className="wedding-card order-form-card">
            <div className="card-header">
              <h2 className="card-title">お客様情報入力</h2>
              <p className="qr-paper-card-description">QRコード用紙をお送りするために <br/>必要な情報をご入力ください</p>
            </div>

            <form onSubmit={handleNext} className="order-form">
              {/* お名前 */}
              <div className="form-group">
                <label className="form-label">
                  <User size={18} />
                  お名前 <span className="required">*</span>
                </label>
                <input
                  type="text"
                  value={formData.userName}
                  onChange={(e) => handleInputChange('userName', e.target.value)}
                  className={`form-input ${errors.userName ? 'error' : ''}`}
                  placeholder="山田 太郎"
                  maxLength={50}
                />
                {errors.userName && (
                  <div className="error-message">
                    <AlertCircle size={16} />
                    {errors.userName}
                  </div>
                )}
              </div>

              <label className="form-label">
                  お届け先住所
              </label>

              {/* 郵便番号 */}
              <div className="form-group">
                <label className="form-label">
                  <MapPin size={18} />
                  郵便番号 <span className="required">*</span>
                </label>
                <div className="postal-code-container">
                  <input
                    type="text"
                    value={formData.postalCode}
                    onChange={(e) => handleInputChange('postalCode', e.target.value)}
                    className={`form-input postal-input ${errors.postalCode ? 'error' : ''}`}
                    placeholder="1234567"
                    maxLength={8}
                  />
                  <button
                    type="button"
                    onClick={searchPostalCode}
                    className="postal-search-button"
                    disabled={isSearchingPostalCode || !formData.postalCode.trim()}
                  >
                    {isSearchingPostalCode ? (
                      <div className="loading-spinner-small"></div>
                    ) : (
                      <>
                        <Search size={16} />
                        住所検索
                      </>
                    )}
                  </button>
                </div>
                {errors.postalCode && (
                  <div className="error-message">
                    <AlertCircle size={16} />
                    {errors.postalCode}
                  </div>
                )}
              </div>

              {/* 都道府県 */}
              <div className="form-group">
                <label className="form-label">
                  <Home size={18} />
                  都道府県 <span className="required">*</span>
                </label>
                <input
                  type="text"
                  value={formData.prefecture}
                  onChange={(e) => handleInputChange('prefecture', e.target.value)}
                  className={`form-input ${errors.prefecture ? 'error' : ''}`}
                  placeholder="東京都"
                  maxLength={10}
                />
                {errors.prefecture && (
                  <div className="error-message">
                    <AlertCircle size={16} />
                    {errors.prefecture}
                  </div>
                )}
              </div>

              {/* 市区町村 */}
              <div className="form-group">
                <label className="form-label">
                  <Home size={18} />
                  市区町村 <span className="required">*</span>
                </label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => handleInputChange('city', e.target.value)}
                  className={`form-input ${errors.city ? 'error' : ''}`}
                  placeholder="渋谷区"
                  maxLength={50}
                />
                {errors.city && (
                  <div className="error-message">
                    <AlertCircle size={16} />
                    {errors.city}
                  </div>
                )}
              </div>

              {/* 番地・建物名 */}
              <div className="form-group">
                <label className="form-label">
                  <Home size={18} />
                  番地・建物名 <span className="required">*</span>
                </label>
                <input
                  type="text"
                  value={formData.addressLine}
                  onChange={(e) => handleInputChange('addressLine', e.target.value)}
                  className={`form-input ${errors.addressLine ? 'error' : ''}`}
                  placeholder="○○1-2-3 ○○マンション101号室"
                  maxLength={100}
                />
                {errors.addressLine && (
                  <div className="error-message">
                    <AlertCircle size={16} />
                    {errors.addressLine}
                  </div>
                )}
              </div>

              {/* メールアドレス */}
              <div className="form-group">
                <label className="form-label">
                  <Mail size={18} />
                  メールアドレス <span className="required">*</span>
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className={`form-input ${errors.email ? 'error' : ''}`}
                  placeholder="example@email.com"
                  maxLength={100}
                />
                {errors.email && (
                  <div className="error-message">
                    <AlertCircle size={16} />
                    {errors.email}
                  </div>
                )}
              </div>

              {/* 注意事項 */}
              <div className="notice-section">
                <div className="notice-header">
                  <AlertCircle size={20} />
                  <span>ご注意事項</span>
                </div>
                <ul className="notice-list">
                  <li>3営業日以内に返信いたします</li>
                  <li>入力いただいた情報は用紙作成・発送の目的のみに使用いたします</li>
                  <li>住所に不備がある場合、発送できない可能性があります</li>
                </ul>
              </div>
            </form>
          </div>

          {/* アクションボタン */}
          <div className="action-buttons">
            <button 
              onClick={handleBack}
              className="wedding-button secondary-button back-button"
            >
              <ArrowLeft size={20} />
              戻る
            </button>
            
            <button 
              onClick={handleNext}
              className="wedding-button primary-button"
            >
              <ArrowRight size={20} />
              次へ
            </button>
          </div>
        </div>

        {/* フッター装飾 */}
        <div className="wedding-footer">
          <div className="footer-decoration">
            <div className="decoration-line"></div>
            <Heart className="footer-heart" />
            <div className="decoration-line"></div>
          </div>
          <p className="footer-text">Beautiful Memories, Perfect Delivery</p>
        </div>
      </div>

    </div>
  );
};

export default QRPaperOrderForm;